// @ts-nocheck
/**
 * Action item cleaner - a one-at-a-time triage deck for a stale backlog.
 *
 * Three keys, no reading a table: left keeps the item untouched, right closes it, down archives
 * it (status 'dropped' - reversible, and it stops nudges/recaps/AI carryover chasing it). Items
 * come oldest first because that is where the dead weight is.
 *
 * Writes go out per swipe rather than batched at the end, so closing the modal half way through
 * keeps the decisions already made. They are fired without blocking the keypress (nobody wants a
 * round trip between cards) but every promise is tracked: failures are counted, surfaced on the
 * summary screen and awaited before the modal reports finished.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@saas-boilerplate/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/components/ui/utils';
import { track } from '@/lib/analytics';
import {
  ArrowLeft,
  ArrowRight,
  ArrowDown,
  Archive,
  Check,
  CornerUpLeft,
  Loader2,
  PartyPopper,
  Sparkles,
  Undo2,
} from 'lucide-react';

export type CleanerItem = {
  id: string;
  title: string;
  status: string;
  created_at: string | null;
  updated_at: string;
  assignee_name: string | null;
  assignee_email: string | null;
  assignee_user_id: string | null;
  meetings: {
    id: string;
    starts_at: string | null;
    meeting_series: { id: string; title: string | null } | null;
  } | null;
};

type Decision = 'keep' | 'done' | 'dropped';

type HistoryEntry = {
  item: CleanerItem;
  decision: Decision;
  /** Status the row held before the swipe, so undo can put it back exactly. */
  previousStatus: string;
};

/** Whole days since the item was captured. */
function ageInDays(createdAt: string | null): number | null {
  if (!createdAt) return null;
  const t = new Date(createdAt).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

function formatAge(days: number | null): string {
  if (days === null) return 'Age unknown';
  if (days === 0) return 'Today';
  if (days === 1) return '1 day old';
  if (days < 14) return `${days} days old`;
  if (days < 60) return `${Math.round(days / 7)} weeks old`;
  if (days < 365) return `${Math.round(days / 30)} months old`;
  const years = days / 365;
  return years < 1.5 ? '1 year old' : `${Math.round(years)} years old`;
}

/** Older items get louder. Keeps the eye moving without needing to read the date. */
function ageTone(days: number | null): { pill: string; rail: string } {
  if (days === null) return { pill: 'bg-muted text-muted-foreground', rail: 'bg-muted-foreground/30' };
  if (days <= 7) return { pill: 'bg-emerald-100 text-emerald-900', rail: 'bg-emerald-400' };
  if (days <= 30) return { pill: 'bg-amber-100 text-amber-900', rail: 'bg-amber-400' };
  if (days <= 90) return { pill: 'bg-orange-100 text-orange-900', rail: 'bg-orange-400' };
  return { pill: 'bg-red-100 text-red-900', rail: 'bg-red-400' };
}

function seriesTitleOf(item: CleanerItem): string {
  const s = item.meetings?.meeting_series;
  return (s && typeof s === 'object' ? s.title : null) || 'Untitled meeting';
}

function sessionDateOf(item: CleanerItem): string | null {
  const startsAt = item.meetings?.starts_at ?? null;
  if (!startsAt) return null;
  const d = new Date(startsAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function assigneeOf(item: CleanerItem): string | null {
  return item.assignee_name?.trim() || item.assignee_email?.trim() || null;
}

const AGE_BUCKETS: { label: string; min: number; max: number }[] = [
  { label: 'Under a week', min: 0, max: 7 },
  { label: '1 to 4 weeks', min: 8, max: 30 },
  { label: '1 to 3 months', min: 31, max: 90 },
  { label: 'Over 3 months', min: 91, max: Number.POSITIVE_INFINITY },
];

/** Fly-away duration. Decorative only: the deck has already advanced by the time it plays. */
const EXIT_MS = 200;

const CLEANER_ANIMATIONS = `
@keyframes cleaner-fly-keep {
  to { transform: translateX(-130%) rotate(-16deg); opacity: 0; }
}
@keyframes cleaner-fly-done {
  to { transform: translateX(130%) rotate(16deg); opacity: 0; }
}
@keyframes cleaner-fly-dropped {
  to { transform: translateY(115%) scale(0.85); opacity: 0; }
}
@keyframes cleaner-card-enter {
  from { transform: scale(0.97); opacity: 0; }
  to { transform: none; opacity: 1; }
}
.cleaner-fly-keep { animation: cleaner-fly-keep ${EXIT_MS}ms ease-in forwards; }
.cleaner-fly-done { animation: cleaner-fly-done ${EXIT_MS}ms ease-in forwards; }
.cleaner-fly-dropped { animation: cleaner-fly-dropped ${EXIT_MS}ms ease-in forwards; }
.cleaner-card { animation: cleaner-card-enter 140ms ease-out; }
@media (prefers-reduced-motion: reduce) {
  .cleaner-fly-keep, .cleaner-fly-done, .cleaner-fly-dropped { animation-duration: 1ms; }
  .cleaner-card { animation: none; }
}
`;

export function ActionItemCleaner({
  open,
  onOpenChange,
  items,
  scopeLabel,
  currentUserId,
  currentUserEmail,
  onFinished,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Candidate rows. Anything already done or archived is ignored. */
  items: CleanerItem[];
  /** Human description of what is in scope, e.g. "in Future of X" or "assigned to you". */
  scopeLabel: string;
  currentUserId: string | null;
  currentUserEmail: string | null;
  /** Called once on close when at least one item changed, so the list can refresh. */
  onFinished: (changed: boolean) => void;
}) {
  const [phase, setPhase] = useState<'intro' | 'running' | 'summary'>('intro');
  const [index, setIndex] = useState(0);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  /** The card that was just decided, flying off screen. Purely decorative. */
  const [ghost, setGhost] = useState<{ item: CleanerItem; dir: Decision; key: string } | null>(null);
  const [dragX, setDragX] = useState(0);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [failures, setFailures] = useState(0);
  const [flushing, setFlushing] = useState(false);

  /** In-flight status writes. Awaited before the summary claims the work is saved. */
  const pending = useRef<Set<Promise<void>>>(new Set());
  const dragStart = useRef<{ x: number; y: number } | null>(null);

  // The deck is frozen when the run starts: rows that arrive later (or statuses we just changed)
  // must not reshuffle the pile under the person's hands.
  const [deck, setDeck] = useState<CleanerItem[]>([]);

  const candidates = useMemo(
    () =>
      items
        .filter((i) => i.status === 'open' || i.status === 'in_progress')
        .slice()
        .sort((a, b) => {
          const at = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bt = b.created_at ? new Date(b.created_at).getTime() : 0;
          return at - bt; // oldest first
        }),
    [items],
  );

  const stats = useMemo(() => {
    const buckets = AGE_BUCKETS.map((b) => ({ ...b, count: 0 }));
    const bySeries = new Map<string, number>();
    let oldest: number | null = null;
    for (const item of candidates) {
      const days = ageInDays(item.created_at);
      if (days !== null) {
        if (oldest === null || days > oldest) oldest = days;
        const bucket = buckets.find((b) => days >= b.min && days <= b.max);
        if (bucket) bucket.count += 1;
      }
      const title = seriesTitleOf(item);
      bySeries.set(title, (bySeries.get(title) || 0) + 1);
    }
    const topSeries = [...bySeries.entries()]
      .map(([title, count]) => ({ title, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 4);
    return { buckets: buckets.filter((b) => b.count > 0), topSeries, oldest, total: candidates.length };
  }, [candidates]);

  // Reset every time the modal is opened so a second run starts from the intro, not mid-deck.
  useEffect(() => {
    if (!open) return;
    setPhase('intro');
    setIndex(0);
    setHistory([]);
    setGhost(null);
    setFailures(0);
    setDragX(0);
    setDragY(0);
  }, [open]);

  const counts = useMemo(() => {
    let kept = 0;
    let done = 0;
    let dropped = 0;
    for (const h of history) {
      if (h.decision === 'keep') kept += 1;
      else if (h.decision === 'done') done += 1;
      else dropped += 1;
    }
    return { kept, done, dropped, cleared: done + dropped };
  }, [history]);

  /** Fire a status write and keep the promise so close/summary can wait on it. */
  const queueWrite = useCallback(
    (itemId: string, nextStatus: string, previousStatus: string) => {
      const run = (async () => {
        const { error } = await supabase
          .from('action_items')
          .update({ status: nextStatus, updated_at: new Date().toISOString() })
          .eq('id', itemId);
        if (error) {
          setFailures((n) => n + 1);
          console.error('[cleaner] status write failed', itemId, error.message);
          return;
        }
        if (currentUserId && nextStatus !== previousStatus) {
          // Same follow-through trail the list writes, so recap and nudge emails show the change.
          const { error: trailError } = await supabase.from('action_item_updates').insert({
            action_item_id: itemId,
            author_user_id: currentUserId,
            author_email: currentUserEmail,
            kind: 'status_change',
            old_status: previousStatus,
            new_status: nextStatus,
            source: 'app',
          });
          if (trailError) console.error('[cleaner] trail insert failed', itemId, trailError.message);
        }
      })();
      pending.current.add(run);
      void run.finally(() => pending.current.delete(run));
    },
    [currentUserId, currentUserEmail],
  );

  /**
   * The deck advances synchronously on every decision. The fly-away is a decorative ghost that
   * animates independently, so nothing is ever gated on an animation finishing - someone
   * hammering the arrow keys through 300 items must never have a keystroke silently swallowed.
   */
  const decide = useCallback(
    (decision: Decision) => {
      const item = deck[index];
      if (!item) return;

      if (decision === 'done') {
        queueWrite(item.id, 'done', item.status);
        track('action_item_completed', { source: 'cleaner' });
      } else if (decision === 'dropped') {
        queueWrite(item.id, 'dropped', item.status);
        track('action_item_archived', { source: 'cleaner' });
      }

      setHistory((h) => [...h, { item, decision, previousStatus: item.status }]);
      setGhost({ item, dir: decision, key: `${item.id}:${Date.now()}` });
      setDragX(0);
      setDragY(0);
      setIndex((i) => i + 1);
    },
    [deck, index, queueWrite],
  );

  const undo = useCallback(() => {
    const last = history[history.length - 1];
    if (!last) return;
    // 'keep' never wrote anything, so there is nothing to reverse.
    if (last.decision !== 'keep') {
      queueWrite(last.item.id, last.previousStatus, last.decision);
    }
    setHistory((h) => h.slice(0, -1));
    setIndex((i) => Math.max(0, i - 1));
    setGhost(null);
  }, [history, queueWrite]);

  const start = useCallback(() => {
    setDeck(candidates);
    setIndex(0);
    setHistory([]);
    setPhase('running');
    track('action_item_cleaner_started', { items: candidates.length });
  }, [candidates]);

  // Everything decided - wait for the writes, then report.
  useEffect(() => {
    if (phase !== 'running') return;
    if (deck.length === 0 || index < deck.length) return;
    let cancelled = false;
    setFlushing(true);
    void Promise.allSettled([...pending.current]).then(() => {
      if (cancelled) return;
      setFlushing(false);
      setPhase('summary');
    });
    return () => {
      cancelled = true;
    };
  }, [phase, index, deck.length]);

  // Keyboard is the primary input; the on-screen buttons mirror it.
  useEffect(() => {
    if (!open || phase !== 'running') return;
    const onKey = (e: KeyboardEvent) => {
      // Held keys would blaze through the deck by accident - deliberate presses only.
      if (e.repeat) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        undo();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          decide('keep');
          break;
        case 'ArrowRight':
          e.preventDefault();
          decide('done');
          break;
        case 'ArrowDown':
          e.preventDefault();
          decide('dropped');
          break;
        case 'Backspace':
          e.preventDefault();
          undo();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, phase, decide, undo]);

  const finishAndClose = useCallback(async () => {
    const changed = counts.cleared > 0;
    if (pending.current.size > 0) {
      setFlushing(true);
      await Promise.allSettled([...pending.current]);
      setFlushing(false);
    }
    onOpenChange(false);
    onFinished(changed);
  }, [counts.cleared, onFinished, onOpenChange]);

  // ── Pointer drag, so the swipe metaphor works with a mouse or a finger too ──
  const onPointerDown = (e: React.PointerEvent) => {
    dragStart.current = { x: e.clientX, y: e.clientY };
    setDragging(true);
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging || !dragStart.current) return;
    setDragX(e.clientX - dragStart.current.x);
    setDragY(Math.max(0, e.clientY - dragStart.current.y));
  };
  const onPointerUp = () => {
    if (!dragging) return;
    setDragging(false);
    dragStart.current = null;
    const horizontal = Math.abs(dragX);
    if (dragY > 110 && dragY > horizontal) decide('dropped');
    else if (dragX > 120) decide('done');
    else if (dragX < -120) decide('keep');
    else {
      setDragX(0);
      setDragY(0);
    }
  };

  const current = deck[index] || null;
  const upNext = deck[index + 1] || null;
  /** Short title of the most recent decision, for the summary screen's undo button. */
  const historyLabel = (() => {
    const last = history[history.length - 1];
    if (!last) return '';
    const t = last.item.title || 'item';
    return t.length > 34 ? `${t.slice(0, 34).trimEnd()}…` : t;
  })();
  const currentAge = current ? ageInDays(current.created_at) : null;
  const tone = ageTone(currentAge);

  /** Which action the current drag would trigger if released now. */
  const dragIntent: Decision | null = (() => {
    if (!dragging) return null;
    if (dragY > 110 && dragY > Math.abs(dragX)) return 'dropped';
    if (dragX > 120) return 'done';
    if (dragX < -120) return 'keep';
    return null;
  })();

  const cardTransform = dragging
    ? `translate(${dragX}px, ${dragY}px) rotate(${dragX / 22}deg)`
    : 'translate(0, 0) rotate(0deg)';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) void finishAndClose();
        else onOpenChange(true);
      }}
    >
      {/* DialogContent's base guard is `max-w-[calc(100%-2rem)] sm:max-w-lg`. Widening it with a
          plain `sm:max-w-2xl` replaces that guard at the sm breakpoint, so between 640px and
          704px the dialog ends up wider than the window. Cap it against the viewport too. */}
      <DialogContent className="max-h-[92vh] min-w-0 overflow-x-hidden overflow-y-auto sm:max-w-[min(42rem,calc(100vw-2rem))]">
        <style>{CLEANER_ANIMATIONS}</style>
        {phase === 'intro' ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Clean up your action items</DialogTitle>
              <DialogDescription>
                One item at a time, three keys. Nothing is deleted for good.
              </DialogDescription>
            </DialogHeader>

            {stats.total === 0 ? (
              <div className="flex flex-col items-center gap-3 py-12 text-center">
                <PartyPopper className="h-10 w-10 text-primary" aria-hidden />
                <p className="text-lg font-medium">Nothing to clean</p>
                <p className="max-w-sm text-sm text-muted-foreground">
                  There are no open action items {scopeLabel}. Come back after a few more meetings.
                </p>
                <Button className="mt-2" onClick={() => void finishAndClose()}>
                  Close
                </Button>
              </div>
            ) : (
              <div className="space-y-5 py-1">
                <div className="rounded-xl border border-outline-variant/15 bg-surface-container-low px-5 py-4">
                  <p className="font-display text-4xl font-semibold text-headline">{stats.total}</p>
                  <p className="text-sm text-muted-foreground">
                    open action items {scopeLabel}
                    {stats.oldest !== null ? `, the oldest ${formatAge(stats.oldest).toLowerCase()}` : ''}
                  </p>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      How old they are
                    </p>
                    <ul className="space-y-1.5">
                      {stats.buckets.map((b) => {
                        const pct = Math.round((b.count / stats.total) * 100);
                        return (
                          <li key={b.label} className="space-y-1">
                            <div className="flex items-baseline justify-between gap-2 text-sm">
                              <span className="text-foreground">{b.label}</span>
                              <span className="tabular-nums text-muted-foreground">{b.count}</span>
                            </div>
                            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                              <div
                                className={cn('h-full rounded-full', ageTone(b.min).rail)}
                                style={{ width: `${Math.max(2, pct)}%` }}
                              />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Where they came from
                    </p>
                    <ul className="space-y-1.5">
                      {stats.topSeries.map((s) => (
                        <li key={s.title} className="flex items-baseline justify-between gap-3 text-sm">
                          <span className="min-w-0 truncate text-foreground">{s.title}</span>
                          <span className="shrink-0 tabular-nums text-muted-foreground">{s.count}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="space-y-2 rounded-xl border border-outline-variant/15 bg-card px-5 py-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    How it works
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-3">
                      <KeyCap><ArrowLeft className="h-3.5 w-3.5" /></KeyCap>
                      <span><strong className="font-medium">Keep</strong> it exactly as it is</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <KeyCap><ArrowRight className="h-3.5 w-3.5" /></KeyCap>
                      <span><strong className="font-medium">Mark done</strong></span>
                    </li>
                    <li className="flex items-center gap-3">
                      <KeyCap><ArrowDown className="h-3.5 w-3.5" /></KeyCap>
                      <span>
                        <strong className="font-medium">Archive</strong> it, so it stops appearing in
                        lists and reminder emails
                      </span>
                    </li>
                    <li className="flex items-center gap-3">
                      <KeyCap className="px-2 text-[10px]">undo</KeyCap>
                      <span>Backspace takes the last one back</span>
                    </li>
                  </ul>
                  <p className="pt-1 text-xs text-muted-foreground">
                    Archived items keep their history and can be reopened from the list at any time.
                  </p>
                </div>

                <Button
                  onClick={start}
                  className="h-auto w-full rounded-lg bg-gradient-to-r from-primary to-[#e9b32e] py-4 text-base font-medium shadow-lg shadow-primary/10"
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Start with the oldest
                </Button>
              </div>
            )}
          </>
        ) : phase === 'running' ? (
          <>
            <DialogHeader className="space-y-3">
              <div className="flex min-w-0 items-baseline justify-between gap-3">
                <DialogTitle className="shrink-0 font-display text-lg">
                  {index + 1} of {deck.length}
                </DialogTitle>
                <span className="truncate text-right text-xs text-muted-foreground">
                  {counts.done} done · {counts.dropped} archived · {counts.kept} kept
                </span>
              </div>
              <Progress value={(index / Math.max(1, deck.length)) * 100} className="h-1.5" />
              <DialogDescription className="sr-only">
                Use the left arrow to keep, the right arrow to mark done, and the down arrow to archive.
              </DialogDescription>
            </DialogHeader>

            {/* Fixed height so the buttons stay put as titles vary in length. overflow-hidden
                keeps the fly-out card from widening the dialog on its way off screen. */}
            <div className="relative min-h-[240px] min-w-0 select-none overflow-hidden px-1 pt-1">
              <div
                key={current?.id || 'empty'}
                role="group"
                aria-label="Action item"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                className={cn(
                  'cleaner-card relative cursor-grab rounded-2xl border border-outline-variant/20 bg-card px-6 py-6 shadow-md active:cursor-grabbing',
                  dragIntent === 'done' && 'border-emerald-400',
                  dragIntent === 'keep' && 'border-primary',
                  dragIntent === 'dropped' && 'border-orange-400',
                )}
                style={{
                  transform: cardTransform,
                  transition: dragging ? 'none' : `transform ${EXIT_MS}ms ease-out`,
                }}
              >
                {current ? (
                  <>
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={cn(
                          'rounded-full px-3 py-1 text-sm font-semibold',
                          tone.pill,
                        )}
                      >
                        {formatAge(currentAge)}
                      </span>
                      {current.status === 'in_progress' ? (
                        <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-900">
                          In progress
                        </span>
                      ) : null}
                      {assigneeOf(current) ? (
                        <span className="text-sm text-muted-foreground">{assigneeOf(current)}</span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Unassigned</span>
                      )}
                    </div>

                    <p className="mt-4 break-words font-display text-2xl font-semibold leading-snug text-headline">
                      {current.title}
                    </p>

                    <p className="mt-3 text-sm text-muted-foreground">
                      {seriesTitleOf(current)}
                      {sessionDateOf(current) ? ` · ${sessionDateOf(current)}` : ''}
                    </p>
                  </>
                ) : null}
              </div>

              {/* Rendered after the live card so it flies out over the top of it. Self-removing,
                  and replaced outright if the next decision lands before it finishes. */}
              {ghost ? (
                <div
                  key={ghost.key}
                  aria-hidden
                  onAnimationEnd={() => setGhost((g) => (g && g.key === ghost.key ? null : g))}
                  className={cn(
                    'pointer-events-none absolute inset-x-0 top-1 rounded-2xl border border-outline-variant/20 bg-card px-6 py-6 shadow-md',
                    `cleaner-fly-${ghost.dir}`,
                  )}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={cn(
                        'rounded-full px-3 py-1 text-sm font-semibold',
                        ageTone(ageInDays(ghost.item.created_at)).pill,
                      )}
                    >
                      {formatAge(ageInDays(ghost.item.created_at))}
                    </span>
                  </div>
                  <p className="mt-4 font-display text-2xl font-semibold leading-snug text-headline">
                    {ghost.item.title}
                  </p>
                </div>
              ) : null}

              {upNext ? (
                <p className="mt-4 max-w-full truncate text-xs text-muted-foreground">
                  Next: {upNext.title}
                </p>
              ) : (
                <p className="mt-4 px-1 text-xs text-muted-foreground">Last one.</p>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 pt-1">
              <SwipeButton
                onClick={() => decide('keep')}
                icon={<ArrowLeft className="h-4 w-4" />}
                label="Keep"
                hint="←"
                className="hover:border-primary hover:text-primary"
              />
              <SwipeButton
                onClick={() => decide('dropped')}
                icon={<Archive className="h-4 w-4" />}
                label="Archive"
                hint="↓"
                className="hover:border-orange-400 hover:text-orange-700"
              />
              <SwipeButton
                onClick={() => decide('done')}
                icon={<Check className="h-4 w-4" />}
                label="Done"
                hint="→"
                className="hover:border-emerald-400 hover:text-emerald-700"
              />
            </div>

            <div className="flex items-center justify-between pt-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={undo}
                disabled={history.length === 0}
                className="text-xs text-muted-foreground"
              >
                <Undo2 className="mr-1.5 h-3.5 w-3.5" />
                Undo last
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setPhase('summary')}
                className="text-xs text-muted-foreground"
              >
                Finish now
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">
                {counts.cleared > 0 ? 'Backlog trimmed' : 'Nothing changed'}
              </DialogTitle>
              <DialogDescription>
                {counts.cleared > 0
                  ? `You cleared ${counts.cleared} of ${history.length} items you looked at.`
                  : 'You kept everything you looked at.'}
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-3 gap-3 py-2">
              <SummaryStat label="Marked done" value={counts.done} tone="text-emerald-700" />
              <SummaryStat label="Archived" value={counts.dropped} tone="text-orange-700" />
              <SummaryStat label="Kept" value={counts.kept} tone="text-foreground" />
            </div>

            {deck.length > index ? (
              <p className="text-sm text-muted-foreground">
                {deck.length - index} items are still waiting. You can pick up where you left off any
                time.
              </p>
            ) : null}

            {failures > 0 ? (
              <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                {failures} {failures === 1 ? 'change' : 'changes'} could not be saved. Those items are
                unchanged in your list.
              </p>
            ) : null}

            <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
              {/* Deciding the last card lands straight here, so the mis-swipe you most want back
                  is the one you can no longer reach from the deck. */}
              {history.length > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    undo();
                    setPhase('running');
                  }}
                  className="mr-auto text-muted-foreground"
                >
                  <Undo2 className="mr-1.5 h-4 w-4" />
                  Undo “{historyLabel}”
                </Button>
              ) : null}
              {deck.length > index ? (
                <Button type="button" variant="outline" onClick={() => setPhase('running')}>
                  <CornerUpLeft className="mr-1.5 h-4 w-4" />
                  Keep going
                </Button>
              ) : null}
              <Button type="button" onClick={() => void finishAndClose()} disabled={flushing}>
                {flushing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                Done
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KeyCap({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        'inline-flex h-6 min-w-6 items-center justify-center rounded border border-outline-variant/40 bg-surface-container-low px-1 font-sans text-muted-foreground shadow-[0_1px_0_rgba(0,0,0,0.08)]',
        className,
      )}
    >
      {children}
    </kbd>
  );
}

function SwipeButton({
  onClick,
  icon,
  label,
  hint,
  className,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  hint: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-center gap-1 rounded-xl border border-outline-variant/25 bg-card py-3 text-sm font-medium text-foreground transition-colors',
        className,
      )}
    >
      <span className="flex items-center gap-1.5">
        {icon}
        {label}
      </span>
      <span className="text-xs text-muted-foreground">{hint}</span>
    </button>
  );
}

function SummaryStat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="rounded-xl border border-outline-variant/15 bg-surface-container-low px-4 py-3 text-center">
      <p className={cn('font-display text-3xl font-semibold', tone)}>{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
